import { useRef, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/toastContext'
import type { StudentDocument, StudentProfile } from '@/features/student/api/profile.api'
import {
  useDeleteDocument,
  useDownloadDocument,
  useDownloadPhoto,
  useDownloadResume,
  useUploadDocument,
} from '@/features/student/hooks/useDocuments'
import { useUploadPhoto, useUploadResume } from '@/features/student/hooks/useProfile'
import { getErrorMessage } from '@/utils/errors'
import { formatDate, humanizeEnum } from '@/utils/format'

function openDownload(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer')
}

function ResumeUploadButton({ label }: { label: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const uploadResume = useUploadResume()
  const toast = useToast()
  return (
    <>
      <Button
        variant="ghost"
        loading={uploadResume.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="d-none"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          uploadResume.mutate(file, { onError: (err) => toast.error(getErrorMessage(err)) })
          e.target.value = ''
        }}
      />
    </>
  )
}

function PhotoUploadButton({ label }: { label: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const uploadPhoto = useUploadPhoto()
  const toast = useToast()
  return (
    <>
      <Button
        variant="ghost"
        loading={uploadPhoto.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        className="d-none"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          uploadPhoto.mutate(file, { onError: (err) => toast.error(getErrorMessage(err)) })
          e.target.value = ''
        }}
      />
    </>
  )
}

export function DocumentsSection({ profile }: { profile: StudentProfile }) {
  const toast = useToast()
  const downloadResume = useDownloadResume()
  const downloadPhoto = useDownloadPhoto()
  const uploadDocument = useUploadDocument()
  const deleteDocument = useDeleteDocument()
  const downloadDocument = useDownloadDocument()
  const documentInputRef = useRef<HTMLInputElement>(null)
  const [documentType, setDocumentType] = useState<StudentDocument['documentType']>('other')

  return (
    <div>
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="border rounded p-3">
            <p className="fw-medium mb-2">Resume</p>
            <div className="d-flex gap-2">
              {profile.hasResume && (
                <Button
                  variant="ghost"
                  loading={downloadResume.isPending}
                  onClick={() =>
                    downloadResume.mutate(undefined, {
                      onSuccess: (result) => openDownload(result.downloadUrl),
                      onError: (e) => toast.error(getErrorMessage(e)),
                    })
                  }
                >
                  Download
                </Button>
              )}
              <ResumeUploadButton label={profile.hasResume ? 'Replace' : 'Upload Resume'} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="border rounded p-3">
            <p className="fw-medium mb-2">Profile Photo</p>
            <div className="d-flex gap-2">
              {profile.hasProfilePhoto && (
                <Button
                  variant="ghost"
                  loading={downloadPhoto.isPending}
                  onClick={() =>
                    downloadPhoto.mutate(undefined, {
                      onSuccess: (result) => openDownload(result.downloadUrl),
                      onError: (e) => toast.error(getErrorMessage(e)),
                    })
                  }
                >
                  View
                </Button>
              )}
              <PhotoUploadButton label={profile.hasProfilePhoto ? 'Replace' : 'Upload Photo'} />
            </div>
          </div>
        </div>
      </div>

      <p className="fw-medium mb-2">Other Documents</p>
      {profile.documents.length === 0 && (
        <p className="text-muted fs-13">No documents uploaded yet.</p>
      )}
      {profile.documents.map((doc) => (
        <div
          key={doc.id}
          className="d-flex justify-content-between align-items-center border-bottom py-2"
        >
          <div>
            <p className="fw-medium mb-0">{doc.originalName}</p>
            <p className="fs-12 text-muted mb-0">
              {humanizeEnum(doc.documentType)} · {formatDate(doc.createdAt)}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="ghost"
              loading={downloadDocument.isPending}
              onClick={() =>
                downloadDocument.mutate(doc.id, {
                  onSuccess: (result) => openDownload(result.downloadUrl),
                  onError: (e) => toast.error(getErrorMessage(e)),
                })
              }
            >
              Download
            </Button>
            <Button
              variant="danger"
              confirmTitle="Delete this document?"
              onClick={() =>
                deleteDocument.mutate(doc.id, { onError: (e) => toast.error(getErrorMessage(e)) })
              }
            >
              Delete
            </Button>
          </div>
        </div>
      ))}

      <div className="d-flex align-items-center gap-2 mt-3">
        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value as StudentDocument['documentType'])}
        >
          <option value="marksheet">Marksheet</option>
          <option value="certificate">Certificate</option>
          <option value="other">Other</option>
        </select>
        <Button
          variant="secondary"
          loading={uploadDocument.isPending}
          onClick={() => documentInputRef.current?.click()}
        >
          Upload Document
        </Button>
        <input
          ref={documentInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="d-none"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (!file) return
            uploadDocument.mutate(
              { file, documentType },
              { onError: (err) => toast.error(getErrorMessage(err)) },
            )
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
